// © Microsoft Corporation. All rights reserved.

import { dispatchAction, getActionPayload } from "./ActionDispatchHelper";

import { ActionDispatchCancellationToken } from "./ActionDispatchCancellationToken";
import { ConnectorConstants } from "../constants/ConnectorConstants";
import { IActionDispatchCancellationToken } from "./IActionDispatchCancellationToken";
import { IActionPayload } from "./IActionPayload";
import { Observer } from "./ActionObserver";
import { PerfTimeline } from "@msnews/diagnostics";

/**
 * Class that wraps the sending of a action to a connector (i.e. dispatch) with an explicit method, so that in the consuming code
 * it is obvious that the action is being "sent"
 * @class
 */
export abstract class ActionSenderBase<TActionSignature extends Function> {
    /**
     * Property which is a function that will send an action to the connector.
     */
    public get send(): TActionSignature {
        return (this.dispatchAction());
    }

    /**
     * Creates an instance of the `ActionSender` class.
     * @constructor
     * @param {string} type The action type.
     * @param {IActionDispatchCancellationToken} cancellationToken Optional cancellation token, which can be used to cancel an in flight action
     */
    constructor(
        protected type: string,
        private observers: Observer[],
        private cancellationToken?: IActionDispatchCancellationToken) { }

    /**
     * Method to get dispatcher and payload
     * @param args - the arguments being dispatched
     * @returns dispatch result metadata
     */
    protected abstract getDispatchResult(args: any[]): { payload: IActionPayload, result: any };

    /**
     * Gets the perf timeline object if applicable
     */
    protected abstract tryGetPerfTimeline(): PerfTimeline;

    /**
     * Method that returns an action sender function to send the action.
     * @method
     * @return {TActionSignature} return value is a function to send to action.
     */
    private dispatchAction(): TActionSignature {
        return <TActionSignature><any> function (): any {
            if (!ActionDispatchCancellationToken.isCancellationRequested(this.cancellationToken)) {

                const perfTimeline = this.tryGetPerfTimeline();
                const actionEndMeasure = perfTimeline && perfTimeline.startMeasure("action", this.type).endMeasure;

                const { payload, dispatchResult } = this.getDispatchResult(Array.prototype.slice.call(arguments));

                // Call any observers
                this.observers.forEach(o => {
                    try {
                        // Call the observer
                        o.observer(payload);
                    } catch (error) {
                        console.log(`Error calling observer: ${JSON.stringify(o)}, with error: ${error}`);
                    }

                    // If there is a connector, dispatch an action to it, forcing it to re-render
                    if (o.connector) {
                        const renderActionPayload = getActionPayload(o.connector, ConnectorConstants.renderAction, [], undefined);
                        dispatchAction(o.connector, renderActionPayload);
                    }
                });

                if (actionEndMeasure) {
                    actionEndMeasure();
                }

                // Return the dispatch result
                return dispatchResult;
            } else {
                // Don't dispatch the action because it has been canceled
                return;
            }
        };
    }

}